'use client';

import classNames from 'classnames';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getActivityType from '@alltrails/core/utils/getActivityType';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Access from '@alltrails/filters/constants/accessEnum';
import Activities from '@alltrails/filters/constants/activitiesEnum';
import excludedActivityTags from '@alltrails/filters/constants/excludedActivityTags';
import type { ActivityType } from '@alltrails/types/ActivityType';
import type TrailFeature from '@alltrails/types/TrailFeature';
import LinkAccordian from '../LinkAccordian';
import MinimizedSeoLinksGrid from '../MinimizedSeoLinksGrid/MinimizedSeoLinksGrid';
import styles from './Tags.module.scss';
import { getFeatureOrAccessLabel, typeToLabel } from './TagsComponents';
import { PointOfInterestTag, Section, SectionType, Tag } from './types';

const TAGS_PER_DESKTOP_GRID_COLUMN = 10;
const TAGS_PER_COLUMN_FOR_BOTS = 50;
const MAX_TAGS_TOTAL_PER_SECTION = 20;

type Props = {
  slug: string;
  tags: Tag[];
  pointsOfInterest?: PointOfInterestTag[];
  isBot?: boolean;
  heading?: string;
  className?: string;
  labelOverrides?: Record<string, string>;
};

export default function Tags({ slug, tags, pointsOfInterest, isBot, heading, className, labelOverrides }: Props) {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const isMobile = useIsMobileSizedScreen();
  const [openAccordions, setOpenAccordions] = useState<boolean[]>(Array.from({ length: 4 }, () => false));

  const activityTags = [] as Activities[];
  const attractionTags = [] as TrailFeature[];
  const allSuitabilityTags = [] as Access[];

  const toggleAccordion = useCallback(
    (index: number) => {
      const newAccordions = [...openAccordions];
      newAccordions[index] = !newAccordions[index];
      setOpenAccordions(newAccordions);
    },
    [openAccordions]
  );

  const filteredTags = tags.filter(tag => !excludedActivityTags.includes(tag as Activities));

  filteredTags.forEach(tag => {
    if (Object.values(Activities).includes(tag as Activities)) {
      activityTags.push(tag as Activities);
    } else if (Object.values(Access).includes(tag as Access)) {
      allSuitabilityTags.push(tag as Access);
    } else {
      attractionTags.push(tag as TrailFeature);
    }
  });

  const collator = new Intl.Collator(languageRegionCode);

  const activities = activityTags
    .map(tag => ({
      label: labelOverrides?.[tag] ?? getActivityType(intl, tag as ActivityType),
      tag
    }))
    .sort((a, b) => collator.compare(a.label, b.label));

  const attractions = attractionTags
    .map(tag => ({
      label: labelOverrides?.[tag] ?? getFeatureOrAccessLabel(intl, tag),
      tag
    }))
    .filter((item): item is { label: string; tag: TrailFeature } => item.label !== null)
    .sort((a, b) => collator.compare(a.label, b.label));
  const suitability = allSuitabilityTags
    .map(tag => ({
      label: labelOverrides?.[tag] ?? getFeatureOrAccessLabel(intl, tag),
      tag
    }))
    .filter((item): item is { label: string; tag: Access } => item.label !== null)
    .sort((a, b) => collator.compare(a.label, b.label));

  const mapEntries = useCallback(
    section => {
      return section.type === 'poi'
        ? section.entries.map(poi => ({ title: poi.name, url: `/poi/${poi.slug}` }))
        : section.entries.map(tag => ({
            title: tag.label,
            url: `/${slug}/${tag.tag}`
          }));
    },
    [slug]
  );

  const renderColumns = useCallback(
    (sections: Section[]) => {
      const mobileColumns: ReactNode[] = [];
      const columns: ReactNode[] = [];
      let remainingColumns = 6;

      sections.forEach((section, index) => {
        mobileColumns.push(
          <LinkAccordian
            key={section.type}
            title={typeToLabel[section.type]}
            isOpen={openAccordions[index]}
            toggleIsOpen={() => toggleAccordion(index)}
            links={mapEntries(section)}
          />
        );
      });

      sections.forEach(section => {
        if (remainingColumns <= 0 || !section.entries.length) {
          return;
        }
        const maxTagsPerColumn = isBot ? TAGS_PER_COLUMN_FOR_BOTS : TAGS_PER_DESKTOP_GRID_COLUMN;
        const sliceMax = Math.min(MAX_TAGS_TOTAL_PER_SECTION, section.entries.length);
        const mappedLinks = mapEntries(section);

        const cappedLinks = mappedLinks.slice(0, sliceMax);
        const canRenderSecondColumn = cappedLinks.length > maxTagsPerColumn && remainingColumns >= 2;

        columns.push(
          <MinimizedSeoLinksGrid
            title={typeToLabel[section.type]}
            key={section.type}
            linkItems={cappedLinks}
            canRenderAsTwoColumns={canRenderSecondColumn}
            isBot={isBot}
            containerClassName={canRenderSecondColumn ? styles.section2 : styles.section1}
          />
        );
        remainingColumns -= canRenderSecondColumn ? 2 : 1;
      });

      return isBot ? (
        <div className={classNames(styles.content, styles.botContent)}>{columns}</div>
      ) : isMobile ? (
        <div>{mobileColumns}</div>
      ) : (
        <div className={styles.content}>{columns}</div>
      );
    },
    [isBot, openAccordions, toggleAccordion, mapEntries, isMobile]
  );

  const renderedContent = useMemo(() => {
    return renderColumns([
      ...(activities.length ? [{ entries: activities, type: 'activity' as SectionType }] : []),
      ...(attractions.length ? [{ entries: attractions, type: 'attraction' as SectionType }] : []),
      ...(suitability.length ? [{ entries: suitability, type: 'suitability' as SectionType }] : []),
      ...(pointsOfInterest && pointsOfInterest.length ? [{ entries: pointsOfInterest, type: 'poi' as SectionType }] : [])
    ]);
  }, [renderColumns, activities, attractions, suitability, pointsOfInterest]);

  return (
    <div className={classNames(styles.container, { [styles.hasHeading]: !!heading }, className)}>
      {heading && (
        <Typography component="h2" variant="heading400" className={styles.heading}>
          {heading}
        </Typography>
      )}
      <div className={styles.renderedContentContainer}>{renderedContent}</div>
    </div>
  );
}
